import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function WebForms() {
  const { siteId } = useParams();
  const [forms, setForms] = useState([]);
  const [name, setName] = useState('Contacto');

  const load = () => {
    api.get(`/tenant/web/sites/${siteId}/forms`)
      .then((r) => setForms(r.data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(load, [siteId]);

  const create = async () => {
    try {
      const { data } = await api.post(`/tenant/web/sites/${siteId}/forms`, {
        name: name.trim() || 'Contacto',
        form_schema: {
          fields: [
            { key: 'name', label: 'Nombre', type: 'text', required: true },
            { key: 'email', label: 'Correo', type: 'email', required: true },
            { key: 'phone', label: 'Teléfono', type: 'tel' },
            { key: 'message', label: 'Mensaje', type: 'textarea' },
          ],
        },
        crm_mapping_json: {
          name: 'name',
          email: 'email',
          phone: 'phone',
          message: 'message',
        },
      });
      toast.success('Formulario creado — los envíos públicos crean leads en el CRM');
      setName('Contacto');
      load();
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const copyEndpoint = (id) => {
    const url = `${window.location.origin}/api/public/web/forms/${id}/submit`;
    navigator.clipboard?.writeText(url);
    toast.success('Dirección copiada — pégala en el atributo action de tu formulario');
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-4" data-testid="web-forms">
      <Link to={`/tenant/web/sites/${siteId}`} className="text-xs text-[#F72585]">← Editor</Link>
      <h1 className="text-2xl font-bold">Formularios</h1>
      <p className="text-sm text-[#64748B]">
        Cada envío crea o actualiza un lead en tu CRM (sin duplicar si la persona ya tiene un lead activo)
        y dispara tus automatizaciones, así que la IA puede contestarle sola. El visitante no elige el
        negocio: el formulario ya está ligado a tu cuenta.
      </p>

      {/* Honestidad sobre el estado real. El texto anterior decía que «el
          visitante usa el widget»; ese widget se demolió con el constructor en
          S77 y nadie corrigió la frase. Prometer una pieza que no existe es
          peor que reconocer el paso manual: quien la busca pierde la tarde. */}
      <div className="border border-emerald-200 bg-emerald-50 rounded-lg px-4 py-3 text-sm text-emerald-950"
           data-testid="web-forms-aviso">
        <p className="font-medium">Al subir tu sitio, los formularios se conectan solos</p>
        <p className="mt-1">
          Al subir un .zip (o con «Detectar y conectar»), el sistema busca las etiquetas
          {' '}<code className="font-mono text-xs bg-white/60 px-1 rounded">{'<form>'}</code>{' '}
          con campos (nombre, correo, mensaje…) y las enlaza a esta lista. Cada envío
          crea la campaña en Fuentes de leads, el webhook de conexión y el lead en tu CRM. Si creas un formulario aquí a mano, sigue
          pudiendo copiar su URL e insertarla tú en el HTML.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre del formulario" />
        <Button onClick={create}>Crear</Button>
        <Button variant="outline" onClick={async () => {
          try {
            const { data } = await api.post(`/tenant/web/sites/${siteId}/forms/auto-connect`);
            const n = (data.connected || []).length;
            const camp = data.campaigns_created || 0;
            const ai = data.ai_assisted || 0;
            toast.success(
              n
                ? `${n} formulario(s) conectado(s)${camp ? `, ${camp} campaña(s) nueva(s)` : ''}${ai ? ` · IA ayudó en ${ai}` : ''}`
                : 'No se encontraron formularios nuevos en el HTML del sitio',
            );
            load();
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
          }
        }} data-testid="web-forms-auto-connect">
          Detectar y conectar del sitio
        </Button>
      </div>

      <ul className="space-y-2">
        {forms.map((f) => (
          <li key={f.id} className="border rounded-lg px-4 py-3 bg-white flex justify-between items-center gap-2">
            <div>
              <p className="font-medium">{f.name}</p>
              <p className="text-xs text-[#64748B]">
                {f.enabled ? 'Activo' : 'Pausado'}
                {f.auto?.wired_by ? ' · conectado al subir el sitio' : ''}
                {' · id '}{f.id.slice(0, 8)}…
              </p>
              {f.auto?.page_path ? (
                <p className="text-[11px] text-[#94A3B8]">Página: {f.auto.page_path}</p>
              ) : null}
              {f.lead_source_id ? (
                <p className="text-[11px] text-emerald-700">
                  Campaña lista · webhook de envío configurado
                  {f.ai_assisted ? ' · mapeo con IA' : ''}
                </p>
              ) : null}
            </div>
            <Button size="sm" variant="outline" onClick={() => copyEndpoint(f.id)}>Copiar URL API</Button>
          </li>
        ))}
        {!forms.length && <li className="text-sm text-[#64748B]">Aún no hay formularios.</li>}
      </ul>
    </div>
  );
}
